import { useState, useEffect, useCallback } from 'react';
import { formatTime } from '../utils/timeUtils';
import useLocalStorage from './useLocalStorage';

/**
 * Custom hook for a robust timer that maintains accuracy even in inactive tabs
 */
const useTimer = ({
  initialSeconds = 0,
  autostart = false,
  onTick = null
} = {}) => {
  // Store timer state in localStorage to persist across tab refreshes
  const [timerState, setTimerState] = useLocalStorage('rugby_timer_state', {
    startTimestamp: null,
    pausedSeconds: initialSeconds,
    isRunning: false
  });
  
  // Current displayed seconds
  const [seconds, setSeconds] = useState(timerState.pausedSeconds);
  
  // Get current seconds based on system time
  const getCurrentSeconds = useCallback(() => {
    if (!timerState.isRunning || !timerState.startTimestamp) {
      return timerState.pausedSeconds;
    }
    
    const now = Date.now();
    const elapsedSeconds = Math.floor((now - timerState.startTimestamp) / 1000);
    return timerState.pausedSeconds + elapsedSeconds;
  }, [timerState]);
  
  // Start the timer
  const startTimer = useCallback(() => {
    if (timerState.isRunning) return;
    
    const now = Date.now();
    setTimerState({
      startTimestamp: now,
      pausedSeconds: timerState.pausedSeconds,
      isRunning: true
    });
  }, [timerState, setTimerState]);
  
  // Pause the timer
  const pauseTimer = useCallback(() => {
    if (!timerState.isRunning) return;
    
    // Calculate current total seconds
    const currentSeconds = getCurrentSeconds();
    
    setTimerState({
      startTimestamp: null,
      pausedSeconds: currentSeconds,
      isRunning: false
    });
    
    // Update displayed seconds
    setSeconds(currentSeconds);
  }, [timerState, getCurrentSeconds, setTimerState]);
  
  // Reset the timer
  const resetTimer = useCallback(() => {
    setTimerState({
      startTimestamp: null,
      pausedSeconds: 0,
      isRunning: false
    });
    
    setSeconds(0);
  }, [setTimerState]);
  
  // Toggle the timer
  const toggleTimer = useCallback(() => {
    if (timerState.isRunning) {
      pauseTimer();
    } else {
      startTimer();
    }
  }, [timerState.isRunning, startTimer, pauseTimer]);
  
  // Set timer to a specific value
  const setTime = useCallback((newSeconds) => {
    const wasRunning = timerState.isRunning;
    
    if (wasRunning) {
      pauseTimer();
    }
    
    setTimerState({
      startTimestamp: wasRunning ? Date.now() : null,
      pausedSeconds: newSeconds,
      isRunning: wasRunning
    });
    
    setSeconds(newSeconds);
  }, [timerState.isRunning, pauseTimer, setTimerState]);
  
  // Update the timer display
  useEffect(() => {
    // Update immediately based on current state
    setSeconds(getCurrentSeconds());
    
    // Only set interval if timer is running
    if (timerState.isRunning) {
      const intervalId = setInterval(() => {
        const currentSeconds = getCurrentSeconds();
        setSeconds(currentSeconds);
        
        if (onTick) {
          onTick(currentSeconds);
        }
      }, 1000); // Update every second
      
      return () => clearInterval(intervalId);
    }
  }, [timerState, getCurrentSeconds, onTick]);
  
  // Handle autostart
  useEffect(() => {
    if (autostart && !timerState.isRunning) {
      startTimer();
    }
  }, [autostart, timerState.isRunning, startTimer]);
  
  // Format time for display
  const formattedTime = formatTime(seconds);
  
  return {
    seconds,
    isRunning: timerState.isRunning,
    formattedTime,
    startTimer,
    pauseTimer,
    resetTimer,
    toggleTimer,
    setTime // Make sure setTime is being returned
  };
};

export default useTimer;